import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { can } from '../utils/roles';
import {
  LayoutDashboard, Users, Phone, ClipboardList, Shield, LogOut,
  Building2, Activity, Briefcase, DollarSign, FileText, Settings, ScrollText,
  Home, Calendar
} from 'lucide-react';

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const isAdmin = user?.role === 'admin';
  const isTopLevMan = user?.role === 'toplevman';
  const isMarketing = user?.role === 'marketing';
  const isManager = user?.role === 'manager' || isAdmin || isTopLevMan;
  const isHRLevel = user?.role === 'hr' || isManager;
  const isHR = user?.role === 'hr' || isManager;

  useEffect(() => {
    api.get('/api/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const navSections = [
    {
      title: 'Main',
      items: [
        { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/leads', icon: ClipboardList, label: 'Leads' },
        { to: '/pipeline', icon: LayoutDashboard, label: 'Pipeline' },
        { to: '/groups', icon: LayoutDashboard, label: 'Lead Groups', show: isAdmin || isTopLevMan || isMarketing },
        { to: '/properties', icon: Home, label: 'Properties' },
        { to: '/meetings', icon: Calendar, label: 'Meetings' },
        { to: '/deals', icon: Briefcase, label: 'Deals' },
        { to: '/calls', icon: Phone, label: 'Calls' },
      ]
    },
    {
      title: 'Management',
      items: [
        { to: '/agents', icon: Users, label: 'Team', show: isManager },
        { to: '/payroll', icon: DollarSign, label: 'Payroll', show: isHRLevel },
        { to: '/reports', icon: FileText, label: 'Reports', show: isManager || isMarketing },
      ]
    },
    {
      title: 'Admin',
      items: [
        { to: '/admin', icon: Shield, label: 'Admin Panel', show: isAdmin },
        { to: '/settings', icon: Settings, label: 'Settings', show: isAdmin },
        { to: '/meta-settings', icon: Settings, label: 'Meta Ads', show: isAdmin || isMarketing },
        { to: '/audit-logs', icon: ScrollText, label: 'Audit Logs', show: isAdmin || isTopLevMan },
      ]
    }
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-dark-900 border-r border-dark-700 z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static lg:z-auto flex flex-col`}>
        <div className="p-4 border-b border-dark-700">
          <div className="flex flex-col items-center gap-2 py-1">
            <div className={`overflow-hidden border border-dark-600 ${
              config?.logo_shape === 'circle' ? 'w-24 h-24 rounded-full' :
              config?.logo_shape === 'rectangle' ? 'w-36 h-20 rounded-xl' :
              'w-24 h-24 rounded-xl'
            }`}>
              <img src="/logo.jpg" alt="Logo" className="w-full h-full object-cover" />
            </div>
            {config?.show_app_name !== false && config?.app_name && (
              <h1 className="text-sm font-bold text-white text-center leading-tight">{config.app_name}</h1>
            )}
            {config?.show_tagline !== false && config?.dashboard_headline && (
              <p className="text-xs text-dark-400 text-center">{config.dashboard_headline}</p>
            )}
          </div>
        </div>

        <nav className="flex-1 p-3 overflow-y-auto space-y-4">
          {navSections.map(section => {
            const visibleItems = section.items.filter(i => i.show !== false);
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.title}>
                <p className="text-xs text-dark-500 font-medium uppercase tracking-wider px-3 mb-1">{section.title}</p>
                <div className="space-y-0.5">
                  {visibleItems.map(({ to, icon: Icon, label }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === '/'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                          isActive
                            ? 'bg-primary-500/10 text-primary-400 font-medium'
                            : 'text-dark-300 hover:text-white hover:bg-dark-800'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-dark-700" style={{cursor:'pointer'}} onClick={() => window.location.href='/profile'}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center overflow-hidden">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
              ) : (
                <span className="text-primary-400 text-sm font-bold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.name}</p>
              <p className="text-xs text-dark-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
